import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsApi } from '../services/api';
import { 
  Trophy, Shirt, Landmark, Tv, Newspaper, Share2, Cloud, Sparkles,
  Wallet, LogOut, Menu, X, User, Shield, Home, Settings, ScrollText,
  Swords, Bell, MessageSquare, Gift
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

const NEW_LOGO_URL = "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/pvmmvvvw__krazy%20bets%20logo%20Final%20png1.png";

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);

  const fetchNotifications = async () => {
    try {
      const { data } = await notificationsApi.getAll(10);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {}
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setShowPanel(!showPanel); if (!showPanel) fetchNotifications(); }}
        className="relative p-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <div className="absolute right-0 top-12 w-[calc(100vw-2rem)] max-w-80 bg-[#2D2D2D] border border-[#D4AF37]/30 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto" data-testid="notification-panel">
          <div className="flex items-center justify-between p-3 border-b border-[#3D3D3D]">
            <span className="text-white font-bold text-sm">Notifications</span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-[#D4AF37] hover:underline">Mark all read</button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">No notifications yet</div>
          ) : (
            notifications.map((n) => (
              <div key={n.id} className={`p-3 border-b border-[#3D3D3D] last:border-0 ${n.read ? 'opacity-60' : ''}`}>
                <div className="font-semibold text-sm text-white break-words">{n.title}</div>
                <div className="text-xs text-gray-400 mt-1 break-words">{n.message}</div>
                <div className="text-xs text-gray-600 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const Header = ({ showAuth = true }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-3">
            <img src={NEW_LOGO_URL} alt="KrazyBets" className="h-12 w-12 drop-shadow-lg" />
            <span className="font-black text-xl uppercase tracking-tight hidden sm:block" style={{ fontFamily: 'Outfit' }}>
              <span className="text-[#D4AF37]">Krazy</span><span className="text-white">Bets</span>
            </span>
          </Link>

          {/* Desktop Nav - Public Pages */}
          {!isAuthenticated && (
            <nav className="hidden md:flex items-center gap-2">
              <Link to="/" className="tab-3d text-sm">
                Home
              </Link>
              <Link to="/bet-on-anything" className="tab-3d text-sm">
                Bet On Anything
              </Link>
              <Link to="/betting-rules" className="tab-3d text-sm">
                Betting Rules
              </Link>
              <Link to="/rps-info" className="tab-3d text-sm">
                Rock Paper Scissor
              </Link>
            </nav>
          )}

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {showAuth && !isAuthenticated && (
              <>
                <Link to="/login">
                  <Button className="tab-3d text-sm text-white" data-testid="login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register" className="hidden sm:block">
                  <Button className="btn-primary text-sm" data-testid="signup-btn">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}

            {isAuthenticated && (
              <>
                <NotificationBell />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    className="tab-3d text-sm text-white"
                    data-testid="user-menu-btn"
                  >
                    <User className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline text-white">{user?.full_name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#2D2D2D] border-[#D4AF37] text-white">
                  <DropdownMenuItem onClick={() => navigate('/wallet')} className="cursor-pointer hover:bg-[#3D3D3D] focus:bg-[#3D3D3D]">
                    <Wallet className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    Wallet
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referrals')} className="cursor-pointer hover:bg-[#3D3D3D] focus:bg-[#3D3D3D]" data-testid="menu-referrals">
                    <Gift className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    Refer & Earn
                  </DropdownMenuItem>
                  {(user?.role === 'admin' || user?.role === 'moderator') && (
                    <>
                      <DropdownMenuSeparator className="bg-[#3D3D3D]" />
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer hover:bg-[#3D3D3D] focus:bg-[#3D3D3D]">
                        <Shield className="mr-2 h-4 w-4 text-[#28A745]" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-[#3D3D3D]" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400 hover:bg-[#3D3D3D] focus:bg-[#3D3D3D]">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            )}

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Public */}
        {mobileMenuOpen && !isAuthenticated && (
          <div className="md:hidden py-4 border-t border-[#D4AF37]/30">
            <nav className="flex flex-col gap-2">
              <Link to="/" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/bet-on-anything" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Bet On Anything</Link>
              <Link to="/betting-rules" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Betting Rules</Link>
              <Link to="/rps-info" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Rock Paper Scissor</Link>
            </nav>
          </div>
        )}

        {/* Mobile Menu - Authenticated */}
        {mobileMenuOpen && isAuthenticated && (
          <div className="md:hidden py-4 border-t border-[#D4AF37]/30">
            <nav className="flex flex-col gap-2">
              <Link to="/dashboard" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
              <Link to="/browse" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Browse Bets</Link>
              <Link to="/create-bet" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Create Bet</Link>
              <Link to="/rps" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Rock Paper Scissors</Link>
              <Link to="/wallet" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>My Wallet</Link>
              <Link to="/referrals" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Refer & Earn</Link>
              <Link to="/tickets" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Support Tickets</Link>
              <Link to="/settings" className="tab-3d text-center" onClick={() => setMobileMenuOpen(false)}>Settings</Link>
              {(user?.role === 'admin' || user?.role === 'moderator') && (
                <Link to="/admin" className="tab-3d text-center text-[#28A745]" onClick={() => setMobileMenuOpen(false)}>Admin Panel</Link>
              )}
              <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="tab-3d text-center text-red-400">Logout</button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Mobile App Bottom Navigation for logged-in users
export const MobileBottomNav = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) return null;

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/browse', icon: Trophy, label: 'Bets' },
    { path: '/wallet', icon: Wallet, label: 'Wallet' },
    { path: '/tickets', icon: MessageSquare, label: 'Tickets' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[#0F0F0F] border-t border-[#D4AF37]/30 md:hidden">
      <div className="flex justify-around items-center h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-all ${
                isActive 
                  ? 'text-[#D4AF37] bg-[#D4AF37]/10' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]' : ''}`} />
              <span className="text-xs mt-1 font-semibold">{item.label}</span>
              {isActive && (
                <div className="absolute bottom-0 w-12 h-1 bg-[#D4AF37] rounded-t-full" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export const Footer = () => (
  <footer className="bg-[#0F0F0F] text-white py-12 mt-auto border-t border-[#D4AF37]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={NEW_LOGO_URL} alt="KrazyBets" className="h-12 w-12" />
            <span className="font-black text-xl uppercase" style={{ fontFamily: 'Outfit' }}>
              <span className="text-[#D4AF37]">Krazy</span><span className="text-white">Bets</span>
            </span>
          </div>
          <p className="text-sm text-gray-400">Bet on Anything. Anytime.</p>
        </div>
        <div>
          <h4 className="font-bold uppercase mb-4 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Categories</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            <li className="hover:text-[#D4AF37] cursor-pointer transition-colors">Sports</li>
            <li className="hover:text-[#D4AF37] cursor-pointer transition-colors">Entertainment</li>
            <li className="hover:text-[#D4AF37] cursor-pointer transition-colors">Weather</li>
            <li className="hover:text-[#D4AF37] cursor-pointer transition-colors">News</li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold uppercase mb-4 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Legal</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            <li><Link to="/terms" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/privacy" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/responsible-gambling" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-responsible-gambling">Responsible Gaming</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold uppercase mb-4 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Support</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            <li><Link to="/help" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-help">Help Center</Link></li>
            <li><Link to="/contact" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-contact">Contact Us</Link></li>
            <li><Link to="/faq" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-faq">FAQ</Link></li>
          </ul>
        </div>
      </div>

      {/* Social Media Icons */}
      <div className="mt-8 pt-8 border-t border-gray-800 flex flex-col items-center gap-6">
        <div className="flex items-center gap-6">
          <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FF0000] transition-colors" data-testid="social-youtube">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          </a>
          <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" data-testid="social-tiktok">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
          </a>
          <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#E4405F] transition-colors" data-testid="social-instagram">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
          </a>
          <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" data-testid="social-x">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
          <a href="https://telegram.org" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0088CC] transition-colors" data-testid="social-telegram">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
          </a>
        </div>
        <p className="text-sm text-gray-500">18+ Only. Gamble Responsibly.</p>
        <p className="text-sm text-[#D4AF37] font-semibold">
          
        </p>
        <p className="text-xs text-gray-600 mt-1">© 2026 KrazyBets. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export const CategoryIcon = ({ category, className = "h-5 w-5" }) => {
  const icons = {
    sports: Trophy,
    fashion: Shirt,
    politics: Landmark,
    entertainment: Tv,
    news: Newspaper,
    social: Share2,
    weather: Cloud,
    wildcard: Sparkles,
    music: '🎵',
    gaming: '🎮'
  };
  
  const Icon = icons[category];
  if (typeof Icon === 'string') {
    return <span className={className}>{Icon}</span>;
  }
  return Icon ? <Icon className={className} /> : <Sparkles className={className} />;
};

export const CategoryBadge = ({ category, selected, onClick }) => {
  const colors = {
    sports: '#28A745',
    fashion: '#EC4899',
    politics: '#6366F1',
    entertainment: '#D4AF37',
    news: '#6F42C1',
    social: '#8B5CF6',
    weather: '#17A2B8',
    wildcard: '#DC3545'
  };

  return (
    <button
      onClick={onClick}
      className={`tab-3d flex items-center gap-2 ${selected ? 'active' : ''}`}
      style={{ borderColor: selected ? colors[category] : 'rgba(212, 175, 55, 0.3)' }}
      data-testid={`category-${category}`}
    >
      <CategoryIcon category={category} />
      <span className="font-semibold capitalize text-sm">{category}</span>
    </button>
  );
};

export default Header;
